import { useState, useRef, useEffect } from "react";
import { useGT } from "gt-react";
import { defaultServerConnectionConfig } from "@spuerhund/shared/targets";
import { useServersStore, useResultStore, selectServer } from "../store";
import { ServerDialog } from "./ServerDialog";
import { ChevronDown, LockKeyhole, Pencil, Plus, Users } from "lucide-react";
import type { StoredServer } from "../persistence";

export function ServerSelector() {
  const t = useGT();
  const servers = useServersStore((s) => s.servers);
  const activeServerId = useServersStore((s) => s.activeServerId);
  const activeServer = servers.find((s) => s.id === activeServerId);
  const connectionStatus = useResultStore((s) => s.connectionStatus);

  const [open, setOpen] = useState(false);
  const [showServerDialog, setShowServerDialog] = useState(false);
  const [editingServer, setEditingServer] = useState<StoredServer | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const activeServerIconClass = connectionStatus === "connected"
    ? "text-success"
    : connectionStatus === "error"
      ? "text-danger"
      : "text-text-muted";

  const handleSelect = async (id: string) => {
    await selectServer(id);
    setOpen(false);
  };

  const handleEdit = (e: React.MouseEvent, server: typeof servers[0]) => {
    e.stopPropagation();
    setEditingServer(server);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-text-muted hover:bg-surface-raised hover:text-text-primary"
      >
        {activeServer ? (
          <>
            <span className="max-w-[180px] truncate text-text-primary">{activeServer.name}</span>
          </>
        ) : (
          <span className="text-text-muted">{t("Select server")}</span>
        )}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[240px] overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-xl">
          {[...servers].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })).map((server) => {
            const isActive = server.id === activeServerId;
            const isShared = Boolean(server.sharedConfigId);
            const ServerScopeIcon = isShared ? Users : LockKeyhole;
            return (
              <div
                key={server.id}
                onClick={() => handleSelect(server.id)}
                className={`group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-surface-overlay text-text-primary"
                    : "text-text-muted hover:bg-surface-raised hover:text-text-primary"
                }`}
              >
                <span
                  className={`mt-0.5 flex shrink-0 items-center justify-center ${
                    isActive ? activeServerIconClass : "text-text-muted/55"
                  }`}
                  title={isShared ? t("Shared server") : t("Private server")}
                >
                  <ServerScopeIcon size={11} strokeWidth={2} />
                </span>
                <div className="flex-1 overflow-hidden">
                  <div className="truncate text-xs font-medium text-text-primary">{server.name}</div>
                  <div className="truncate text-[10px] text-text-muted">
                    {(() => {
                      const connection = defaultServerConnectionConfig(server);
                      if (connection.kind === "mongo") return `MongoDB${connection.database ? ` · ${connection.database}` : ""}`;
                      if (connection.kind === "postgres") return `PostgreSQL${connection.defaultSchema ? ` · ${connection.defaultSchema}` : ""}`;
                      return server.baseUrl;
                    })()}
                  </div>
                </div>
                <div className="flex shrink-0 gap-0.5 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => handleEdit(e, server)}
                    className="rounded p-1 text-text-muted transition-colors hover:bg-surface-overlay hover:text-text-primary"
                    title={t("Edit server")}
                  >
                    <Pencil size={10} />
                  </button>
                </div>
              </div>
            );
          })}

          {servers.length > 0 && <div className="my-1 border-t border-border" />}

          <button
            onClick={() => { setShowServerDialog(true); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-text-muted transition-colors hover:bg-surface-raised hover:text-text-primary"
          >
            <Plus size={12} />
            {t("Add Server")}
          </button>
        </div>
      )}

      {showServerDialog && (
        <ServerDialog onClose={() => setShowServerDialog(false)} />
      )}

      {editingServer && (
        <ServerDialog
          editingServer={editingServer}
          onClose={() => setEditingServer(null)}
        />
      )}
    </div>
  );
}
